import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { RsIcon } from '../icons'
import { PORTAL_NAV_ICONS } from '../icons/registry'
import {
  getPortalNav,
  preparePortalNav,
  portalSubtitle,
  isPortalNavActive,
  isNavItemLocked,
  PORTAL_ROLES,
} from './portalNav'

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
  { path: '/chat', label: 'Chat', icon: 'chat' },
  { path: '/documents', label: 'Documents', icon: 'files' },
  { path: '/contracts', label: 'Contracts', icon: 'contracts' },
  { path: '/email-campaigns', label: 'Email Campaigns', icon: 'email' },
  { path: '/listings', label: 'Listings', icon: 'listings' },
  { path: '/deals', label: 'Deals', icon: 'deal' },
  { path: '/assets', label: 'Assets', icon: 'portfolio' },
  { path: '/payment', label: 'Payment', icon: 'payment' },
  { path: '/settings', label: 'Settings', icon: 'settings' },
]

const ADMIN_ITEMS = [
  { path: '/admin', label: 'Admin Panel', icon: 'admin', exact: true },
  { path: '/admin/digest', label: 'Digest Config', icon: 'digest' },
  { path: '/admin/cms', label: 'Content & Templates', icon: 'templates' },
  { path: '/admin/moderation', label: 'Moderation', icon: 'moderation' },
]

function resolveActivePath(pathname, items) {
  const sorted = [...items].sort((a, b) => b.path.length - a.path.length)
  for (const item of sorted) {
    if (item.exact) {
      if (pathname === item.path) return item.path
      continue
    }
    if (pathname === item.path || pathname.startsWith(`${item.path}/`)) {
      return item.path
    }
  }
  return null
}

const linkClass = (active) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-white/25 ${
    active ? 'bg-primary-500 text-white' : 'text-gray-300 hover:bg-white/5 hover:text-white'
  }`

export default function Sidebar() {
  const { user, logout, isAdmin, role } = useAuth()
  const location = useLocation()
  const [unreadCount, setUnreadCount] = useState(0)
  const [digestUnread, setDigestUnread] = useState(0)

  const portalNav = !isAdmin ? preparePortalNav(getPortalNav(role)) : null
  const isPortal = Boolean(portalNav)
  const sub = isPortal ? portalSubtitle(role) : null

  useEffect(() => {
    if (!user) return
    const load = () => {
      axios.get('/api/chat/unread-count').then((r) => setUnreadCount(r.data.count || 0)).catch(() => {})
    }
    load()
    const id = setInterval(load, 30000)
    window.addEventListener('rs-chat-unread-changed', load)
    window.addEventListener('rs-notifications-changed', load)
    return () => {
      clearInterval(id)
      window.removeEventListener('rs-chat-unread-changed', load)
      window.removeEventListener('rs-notifications-changed', load)
    }
  }, [user?.id])

  useEffect(() => {
    if (!user || !getPortalNav(role)) return
    const loadDigestUnread = () => {
      axios.get('/api/digest/unread-count')
        .then((r) => setDigestUnread(r.data.count || 0))
        .catch(() => setDigestUnread(0))
    }
    loadDigestUnread()
    window.addEventListener('rs-digest-read-changed', loadDigestUnread)
    window.addEventListener('rs-notifications-changed', loadDigestUnread)
    return () => {
      window.removeEventListener('rs-digest-read-changed', loadDigestUnread)
      window.removeEventListener('rs-notifications-changed', loadDigestUnread)
    }
  }, [user?.id, role])

  const activeNavPath = resolveActivePath(location.pathname, NAV_ITEMS)
  const activeAdminPath = isAdmin ? resolveActivePath(location.pathname, ADMIN_ITEMS) : null
  const searchParams = new URLSearchParams(location.search)

  if (isPortal && portalNav) {
    return (
      <aside className="portal-sidebar">
        <div className="portal-sidebar-logo">
          <Link to="/dashboard" style={{ color: 'inherit', textDecoration: 'none' }}>
            <h1>RotationStreet</h1>
            <span>{sub}</span>
          </Link>
        </div>
        <nav style={{ flex: 1, overflowY: 'auto' }}>
          {portalNav.map((item, idx) => {
            if (item.divider) {
              return <div key={`d-${idx}`} className="portal-nav-divider" />
            }
            const active = isPortalNavActive(item, location.pathname, searchParams)
            const lockType = isNavItemLocked(item, user)
            const locked = Boolean(lockType)
            const showUnread = item.unreadKey === 'chat' && unreadCount > 0 && !locked
            const showDigestUnread = item.unreadKey === 'digest' && digestUnread > 0 && !locked
            const navIcon = PORTAL_NAV_ICONS[item.featureKey] || 'home'
            const className = `portal-nav-item${active ? ' active' : ''}${locked ? ' locked' : ''}`
            const inner = (
              <>
                <RsIcon name={navIcon} size="sm" />
                <span style={{ flex: 1 }}>{item.label}</span>
                {locked && (
                  <span
                    className="portal-nav-lock"
                    title={lockType === 'admin' ? 'Not enabled for your account' : 'Upgrade plan required'}
                  >
                    <RsIcon name="lock" size="xs" />
                  </span>
                )}
                {showUnread && (
                  <span className="portal-nav-badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
                {showDigestUnread && (
                  <span className="portal-nav-badge">
                    {digestUnread > 99 ? '99+' : digestUnread}
                  </span>
                )}
              </>
            )
            return (
              <Link
                key={item.path + item.label}
                to={item.path}
                className={className}
                aria-label={locked ? `${item.label} (locked)` : item.label}
              >
                {inner}
              </Link>
            )
          })}
        </nav>
        <div className="portal-sidebar-footer">
          <button
            type="button"
            onClick={logout}
            className="portal-nav-item"
            style={{ padding: '10px 0' }}
          >
            <RsIcon name="logout" size="sm" />
            Log Out
          </button>
        </div>
      </aside>
    )
  }

  return (
    <div className="fixed left-0 top-0 w-[240px] h-screen bg-navy text-white flex flex-col z-40">
      <div className="p-5 border-b border-white/10">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
            <RsIcon name="globe" size="md" className="text-white" />
          </div>
          <span className="text-lg font-bold">RotationStreet</span>
        </Link>
      </div>

      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-sm font-bold">
            {user?.real_name?.charAt(0) || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{user?.system_name || user?.real_name}</p>
            <p className="text-xs text-gray-400">{role} {user?.is_paid && '★'}</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 py-4 overflow-y-auto scrollbar-sidebar pr-1">
        <div className="px-3 space-y-1">
          {NAV_ITEMS.filter((item) => !(isAdmin && item.path === '/dashboard')).map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={linkClass(activeNavPath === item.path)}
            >
              <RsIcon name={item.icon} size="md" />
              <span>{item.label}</span>
              {item.path === '/chat' && unreadCount > 0 && (
                <span className="ml-auto min-w-[20px] h-5 px-1 bg-red-500 rounded-full text-xs flex items-center justify-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
          ))}
        </div>

        {isAdmin && (
          <div className="mt-6 px-3">
            <p className="px-3 text-xs font-semibold text-gray-500 uppercase mb-2">Admin</p>
            <div className="space-y-1">
              {ADMIN_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={linkClass(activeAdminPath === item.path)}
                >
                  <RsIcon name={item.icon} size="md" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      <div className="p-4 border-t border-white/10">
        <button
          onClick={logout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors w-full"
        >
          <RsIcon name="logout" size="md" />
          <span>Log Out</span>
        </button>
      </div>
    </div>
  )
}
